import React, { useContext } from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { LocalizeContext, LocalizeContextProps, Translate } from 'react-localize-redux';
import WaitTimeDistributionChart from './WaitTimeDistribution';
import WaitTimeChart, { IAppointmentWaitTimeStats } from './WaitTimeChart';
import { computeWaitTimeSummary } from '../utils/waitTimeSummary';

interface WaitTimeSectionProps {
  data: IAppointmentWaitTimeStats;
  title?: string;
}

const MetricCard = ({ label, value }: { label: React.ReactNode; value: string | number }) => (
  <Paper sx={{ p: 2, height: '100%' }}>
    <Typography variant="body2" color="text.secondary" gutterBottom>
      {label}
    </Typography>
    <Typography variant="h5">{value}</Typography>
  </Paper>
);

const WaitTimeSection = ({ data, title }: WaitTimeSectionProps) => {
  // SHER-828: getTranslate is a selector, not a hook; LocalizeContext is missing from the
  // package's typings. See analyticsTranslate.sher828.test.tsx.
  const { translate } = useContext(LocalizeContext as React.Context<LocalizeContextProps>);
  const summary = computeWaitTimeSummary(data.waitTimeStats || []);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          {title || <Translate id="hospital.analytics.wait_time.section_title" />}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <Translate id="hospital.analytics.wait_time.section_subtitle" />
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label={<Translate id="hospital.analytics.wait_time.total_appointments" />}
            value={summary.totalAppointments}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label={<Translate id="hospital.analytics.wait_time.average_days" />}
            value={summary.averageWaitDays}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label={<Translate id="hospital.analytics.wait_time.median_days" />}
            value={summary.medianWaitDays}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            label={<Translate id="hospital.analytics.wait_time.over_one_month_percent" />}
            value={`${summary.overOneMonthPercent}%`}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <WaitTimeDistributionChart
            data={data}
            title={translate('hospital.analytics.wait_time.distribution_title') as string}
            chartType="bar"
            horizontal
            usePercentage
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <WaitTimeChart data={data} horizontal metricType="avg" />
        </Grid>
      </Grid>
    </Box>
  );
};

export default WaitTimeSection;
